const EMOTIONAL_AXES = [
  'warmth',
  'futurity',
  'chaos',
  'sacredness',
  'organicness',
  'nostalgia',
  'surrealism',
  'isolation',
  'emotionalSaturation',
  'corporateDensity'
]

// Hybrid archetype templates based on axis combinations
const HYBRID_ARCHETYPE_TEMPLATES = {
  // Cyberpunk + Sacred
  'cyberpunk_sacred': {
    pattern: ['futurity', 'chaos', 'sacredness'],
    template: '{primary} Ritualism',
    description: 'Where digital transcendence meets ancient ceremony'
  },
  // Pastoral + Ruin
  'pastoral_ruin': {
    pattern: ['organicness', 'nostalgia', 'isolation'],
    template: 'Forgotten {primary}',
    description: 'Nature reclaiming forgotten places'
  },
  // Corporate + Surreal
  'corporate_surreal': {
    pattern: ['corporateDensity', 'surrealism', 'futurity'],
    template: 'Dream {primary}',
    description: 'Bureaucracy twisted by impossible logic'
  }
}

// Transformation pathway templates
const TRANSFORMATION_PATHWAYS = {
  'pastoral': [
    { stage: 'Sacred Pastoral', tension: 0.3 },
    { stage: 'Ritual Ecology', tension: 0.6 },
    { stage: 'Organic Mysticism', tension: 0.9 }
  ],
  'cyberpunk': [
    { stage: 'Signal Ruin', tension: 0.3 },
    { stage: 'Static Veil', tension: 0.6 },
    { stage: 'Machine Melancholia', tension: 0.9 }
  ]
}

export default class SymbolicEvolutionLab {
  constructor() {
    this.state = {
      // Global emotional modifiers
      emotionalModifiers: EMOTIONAL_AXES.reduce((acc, axis) => {
        acc[axis] = 0 // Range: -1.0 to 1.0
        return acc
      }, {}),
      
      // Experimental mode settings
      isFrozen: false,
      isExperimentalMode: false,
      
      // Selected territories for hybridization
      selectedTerritories: new Set(),
      
      // Active perturbations
      perturbations: new Map()
    }
  }

  // Apply global emotional modifiers to the system
  applyGlobalModifiers(vibes) {
    if (this.state.isFrozen) return vibes
    
    return vibes.map(vibe => {
      const modifiedAxes = { ...vibe.axes }
      
      // Apply each emotional modifier
      Object.entries(this.state.emotionalModifiers).forEach(([axis, modifier]) => {
        if (modifier !== 0) {
          // Apply modifier with clamping to keep values in 0-100 range
          modifiedAxes[axis] = Math.max(0, Math.min(100, (vibe.axes[axis] || 0) + (modifier * 50)))
        }
      })
      
      return {
        ...vibe,
        axes: modifiedAxes
      }
    })
  }

  // Select territories for hybridization
  selectTerritory(territoryId, isSelected) {
    if (isSelected) {
      this.state.selectedTerritories.add(territoryId)
    } else {
      this.state.selectedTerritories.delete(territoryId)
    }
  }

  // Get currently selected territories
  getSelectedTerritories() {
    return Array.from(this.state.selectedTerritories)
  }

  // Generate hybrid territory from selected territories
  generateHybrid(territories, interpretationEngine) {
    const selectedTerritories = Array.from(this.state.selectedTerritories)
      .map(id => interpretationEngine.getById(id))
      .filter(Boolean)
    
    if (selectedTerritories.length < 2) {
      return null
    }
    
    // Calculate average axes across selected territories
    const avgAxes = EMOTIONAL_AXES.reduce((acc, axis) => {
      const sum = selectedTerritories.reduce((sum, territory) => {
        return sum + (territory.averageAxes[axis] || 0)
      }, 0)
      acc[axis] = sum / selectedTerritories.length
      return acc
    }, {})
    
    // Determine hybrid archetype based on dominant axes
    const dominantAxes = Object.entries(avgAxes)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([axis]) => axis)
    
    // Generate hybrid name based on dominant axes
    const primaryAxis = dominantAxes[0]
    const secondaryAxis = dominantAxes[1]
    
    // Simple hybrid naming convention
    const axisLabels = {
      warmth: 'Warmth',
      futurity: 'Future',
      chaos: 'Chaos',
      sacredness: 'Sacred',
      organicness: 'Organic',
      nostalgia: 'Nostalgic',
      surrealism: 'Surreal',
      isolation: 'Isolated',
      emotionalSaturation: 'Emotional',
      corporateDensity: 'Corporate'
    }
    
    const hybridName = `${axisLabels[primaryAxis] || primaryAxis} ${axisLabels[secondaryAxis] || secondaryAxis} Hybrid`
    
    // Generate hybrid archetype
    const hybridArchetype = this.generateHybridArchetype(primaryAxis, secondaryAxis, avgAxes)
    
    // Generate symbolic climate
    const symbolicClimate = this.generateSymbolicClimate(primaryAxis, secondaryAxis, avgAxes)
    
    return {
      id: `hybrid-${Date.now()}`,
      title: hybridName,
      symbolicArchetype: hybridArchetype,
      averageAxes: avgAxes,
      symbolicClimate: symbolicClimate,
      dominantAxes: dominantAxes.map(axis => ({
        axis,
        label: axisLabels[axis] || axis,
        value: avgAxes[axis]
      })),
      isHybrid: true,
      sourceTerritories: selectedTerritories.map(t => t.id)
    }
  }

  // Generate hybrid archetype based on axes
  generateHybridArchetype(primaryAxis, secondaryAxis, avgAxes) {
    const tension = Object.values(avgAxes).reduce((sum, val) => sum + Math.abs(50 - val), 0) / (100 * Object.keys(avgAxes).length)
    
    if (tension > 0.4) {
      return `${primaryAxis} frontier under ${secondaryAxis} strain`
    }
    
    if (Object.values(avgAxes).some(val => val > 80)) {
      return `${primaryAxis} enclave with ${secondaryAxis} rites`
    }
    
    return `${primaryAxis} province of ${secondaryAxis} drift`
  }

  // Generate symbolic climate description
  generateSymbolicClimate(primaryAxis, secondaryAxis, avgAxes) {
    const avgPrimary = avgAxes[primaryAxis] || 0
    const avgSecondary = avgAxes[secondaryAxis] || 0
    
    const intensity = avgPrimary > 70 ? 'intense' : avgPrimary > 30 ? 'moderate' : 'subtle'
    const stability = Math.abs(avgPrimary - avgSecondary) < 20 ? 'balanced' : 'unstable'
    
    return `${intensity} ${primaryAxis} under ${stability} ${secondaryAxis} influence`
  }

  // Get transformation pathways for a territory
  getTransformationPathways(territory) {
    // This would be more sophisticated in a full implementation
    // For now, we'll return some example pathways
    const primaryAxis = territory.dominantAxes[0]?.axis
    
    if (TRANSFORMATION_PATHWAYS[primaryAxis]) {
      return TRANSFORMATION_PATHWAYS[primaryAxis]
    }
    
    // Generate generic pathways based on tension
    const tension = territory.tension || 0
    return [
      { stage: `${territory.title} Shift`, tension: tension * 0.5 },
      { stage: `${territory.title} Evolution`, tension: tension * 0.75 },
      { stage: `${territory.title} Transformation`, tension: tension }
    ]
  }

  // Compare two territories for compatibility
  compareTerritories(territoryA, territoryB, genomeEngine) {
    if (!territoryA || !territoryB) return null
    
    // Calculate emotional similarity
    const emotionalSimilarity = genomeEngine.calculateSimilarity(
      territoryA.averageAxes,
      territoryB.averageAxes
    )
    
    // Calculate axis compatibility
    const axisCompatibility = this.calculateAxisCompatibility(
      territoryA.averageAxes,
      territoryB.averageAxes
    )
    
    // Calculate tension contrast
    const tensionContrast = Math.abs(
      (territoryA.tension || 0) - (territoryB.tension || 0)
    )
    
    return {
      emotionalSimilarity,
      axisCompatibility,
      tensionContrast,
      overallCompatibility: (emotionalSimilarity * 0.6 + axisCompatibility * 0.4),
      description: this.generateCompatibilityDescription(
        emotionalSimilarity,
        axisCompatibility,
        tensionContrast
      )
    }
  }

  // Calculate axis compatibility between two territories
  calculateAxisCompatibility(axesA, axesB) {
    let totalCompatibility = 0
    let axisCount = 0
    
    EMOTIONAL_AXES.forEach(axis => {
      const valueA = axesA[axis] || 0
      const valueB = axesB[axis] || 0
      
      // Compatibility is higher when values are closer
      const difference = Math.abs(valueA - valueB)
      const compatibility = 1 - (difference / 100)
      
      totalCompatibility += compatibility
      axisCount++
    })
    
    return axisCount > 0 ? totalCompatibility / axisCount : 0
  }

  // Generate compatibility description
  generateCompatibilityDescription(similarity, compatibility, tensionContrast) {
    if (similarity > 0.8) {
      return 'Highly compatible symbolic structures'
    }
    
    if (compatibility > 0.7) {
      return 'Complementary emotional profiles'
    }
    
    if (tensionContrast > 0.5) {
      return 'Tension creates dynamic interaction'
    }
    
    return 'Neutral compatibility with potential for hybridization'
  }

  // Identify unexplored symbolic regions
  findUnexploredRegions(vibes, territories) {
    // This would analyze the current vibe distribution to find gaps
    // For now, we'll return some example findings
    
    const avgAxes = EMOTIONAL_AXES.reduce((acc, axis) => {
      const sum = vibes.reduce((sum, vibe) => sum + (vibe.axes[axis] || 0), 0)
      acc[axis] = sum / vibes.length
      return acc
    }, {})
    
    // Find underrepresented axes
    const underrepresented = Object.entries(avgAxes)
      .filter(([axis, value]) => value < 30)
      .map(([axis]) => axis)
    
    // Find overrepresented axes
    const overrepresented = Object.entries(avgAxes)
      .filter(([axis, value]) => value > 70)
      .map(([axis]) => axis)
    
    return {
      underrepresented,
      overrepresented,
      rareCombinations: this.findRareCombinations(territories),
      suggestions: [
        `Explore low ${underrepresented[0] || 'warmth'} territories`,
        `Investigate ${overrepresented[0] || 'futurity'}-dominated regions`
      ]
    }
  }

  // Find rare axis combinations in territories
  findRareCombinations(territories) {
    // Simple implementation - in a full system this would be more sophisticated
    const combinations = []
    
    territories.forEach(territory => {
      const highAxes = Object.entries(territory.averageAxes || {})
        .filter(([axis, value]) => value > 80)
        .map(([axis]) => axis)
      
      if (highAxes.length >= 3) {
        combinations.push({
          territory: territory.title,
          axes: highAxes,
          description: `Rare convergence of ${highAxes.join(', ')}`
        })
      }
    })
    
    return combinations
  }

  // Update lab state
  updateState(updates) {
    Object.assign(this.state, updates)
  }

  // Get current lab state
  getState() {
    return { ...this.state }
  }

  // Reset all modifiers and selections
  reset() {
    this.state.emotionalModifiers = EMOTIONAL_AXES.reduce((acc, axis) => {
      acc[axis] = 0
      return acc
    }, {})
    
    this.state.selectedTerritories.clear()
    this.state.isFrozen = false
    this.state.isExperimentalMode = false
  }
}